export const ACTIVITY_TYPE_MEETING = 2;
export const ACTIVITY_TYPE_CALL = 3;

const ELIGIBLE_ACTIVITY_TYPES = new Set([
  String(ACTIVITY_TYPE_MEETING),
  String(ACTIVITY_TYPE_CALL),
  'MEETING',
  'CALL',
  'PHONE_CALL',
  'PHONE CALL',
]);

export type ActivityDoneTriggerInput = {
  wasDone: boolean;
  isDone: boolean;
  activityType?: unknown;
  milestoneEvent?: unknown;
};

export function shouldEvaluateActivityDone(input: ActivityDoneTriggerInput): boolean {
  if (input.wasDone || !input.isDone) {
    return false;
  }
  if (hasMilestoneEvent(input.milestoneEvent)) {
    return false;
  }
  return isMeetingOrCall(input.activityType);
}

function hasMilestoneEvent(milestoneEvent: unknown): boolean {
  if (milestoneEvent == null || milestoneEvent === '') {
    return false;
  }
  return milestoneEvent !== 0 && milestoneEvent !== '0';
}

export function hasActivityId(id: unknown): boolean {
  return id != null && id !== '';
}

export type ActivityDoneSavedResult = {
  id?: unknown;
  communicationId?: unknown;
  isDone?: boolean;
  activityType?: unknown;
  milestoneEvent?: unknown;
};

/**
 * Create-and-done has no id on the form payload. Evaluate needs the persisted id,
 * the same as Mark as Done on an existing activity.
 */
export function activityForDoneTrigger<T extends {
  id?: unknown;
  isDone?: boolean;
  activityType?: unknown;
  milestoneEvent?: unknown;
}>(submitted: T, saved: ActivityDoneSavedResult | ActivityDoneSavedResult[] | undefined | null): T {
  const row = Array.isArray(saved) ? saved[0] : saved;
  const savedId = hasActivityId(row?.id) ? row!.id
    : hasActivityId(row?.communicationId) ? row!.communicationId
      : undefined;
  return {
    ...submitted,
    id: (hasActivityId(savedId) ? savedId : submitted.id) as T['id'],
    isDone: submitted.isDone ?? !!row?.isDone,
    activityType: submitted.activityType != null && submitted.activityType !== ''
      ? submitted.activityType
      : row?.activityType,
    milestoneEvent: submitted.milestoneEvent ?? row?.milestoneEvent,
  };
}

export function isMeetingOrCall(activityType: unknown): boolean {
  if (activityType == null || activityType === '') {
    return false;
  }
  if (typeof activityType === 'object') {
    const typed = activityType as { id?: unknown; code?: unknown; name?: unknown };
    return isMeetingOrCall(typed.id) || isMeetingOrCall(typed.code) || isMeetingOrCall(typed.name);
  }
  const normalized = String(activityType).trim().toUpperCase().replace(/_/g, ' ');
  return ELIGIBLE_ACTIVITY_TYPES.has(normalized)
    || ELIGIBLE_ACTIVITY_TYPES.has(normalized.replace(' ', '_'));
}
